import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import PatronDisplay from './components/PatronDisplay';
import ShopView from './components/ShopView';
import MemoriesView from './components/MemoriesView';
import FeedView from './components/FeedView';
import Wardrobe from './components/Wardrobe';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';
const REFRESH_MS = 30000;

function App() {
  // userId вычисляем синхронно при первом рендере: telegram-web-app.js подключён
  // в <head> без async, значит window.Telegram.WebApp уже готов к моменту старта React.
  const [userId] = useState(getUserId);

  const [patron, setPatron] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [view, setView] = useState('patron');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadPatron = useCallback(async () => {
    if (!userId) {
      setError('⚠️ Не удалось определить пользователя. Открой приложение через кнопку в боте.');
      setLoading(false);
      return;
    }

    try {
      const data = await apiGet(`/patron?user_id=${userId}`);
      setPatron(data.patron);
      setInventory(data.inventory || []);
      setError(null);
    } catch (err) {
      console.error('loadPatron failed:', err);
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadPatron();
    const interval = setInterval(loadPatron, REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadPatron]);

  const handleFeed = useCallback(async (text, emoji) => {
    try {
      const data = await apiPost('/feed', { user_id: userId, text, emoji });
      if (data.patron) {
        setPatron(data.patron);
        setError(null);
      }
      return data;
    } catch (err) {
      console.error('feed failed:', err);
      setError(describeError(err));
      return null;
    }
  }, [userId]);

  const handleBuy = useCallback(async (itemId) => {
    try {
      const data = await apiPost('/buy', { user_id: userId, item_id: itemId });
      setPatron(data.patron);
      setInventory(data.inventory || []);
      setError(null);
      return data;
    } catch (err) {
      console.error('buy failed:', err);
      // Магазину нужен машинный код ошибки, чтобы показать свой текст
      return { error: err.message };
    }
  }, [userId]);

  const handleEquip = useCallback(async (itemId, equip, color) => {
    try {
      const data = await apiPost('/equip', {
        user_id: userId, item_id: itemId, equip, color: color || '',
      });
      setPatron(data.patron);
      setInventory(data.inventory || []);
      setError(null);
    } catch (err) {
      console.error('equip failed:', err);
      setError(describeError(err));
    }
  }, [userId]);

  const handleColor = useCallback(async (color) => {
    try {
      const data = await apiPost('/equip', { user_id: userId, item_id: 0, color });
      setPatron(data.patron);
      setInventory(data.inventory || []);
    } catch (err) {
      console.error('color failed:', err);
      setError(describeError(err));
    }
  }, [userId]);

  if (loading) {
    return <div className="app loading">⏳ Загружаю твоего патронуса...</div>;
  }

  if (!patron) {
    return (
      <div className="app error">
        <div className="fatal-error">
          <h2>😢 Не получилось загрузить</h2>
          <p>{error}</p>
          <button onClick={() => { setLoading(true); loadPatron(); }}>🔄 Повторить</button>
        </div>
      </div>
    );
  }

  const navItems = [
    { id: 'patron', label: 'Патронус', icon: '🌟' },
    { id: 'feed', label: 'Записать', icon: '📝' },
    { id: 'wardrobe', label: 'Гардероб', icon: '👗' },
    { id: 'shop', label: 'Лавка', icon: '🛍️' },
    { id: 'memories', label: 'Моменты', icon: '💭' },
  ];

  return (
    <div className="app">
      <header className="app-header">
        <h1>🦁 Патронус</h1>
        <div className="header-stats">
          <span>❤️ {patron.happiness}</span>
          <span>💰 {patron.coins}</span>
        </div>
      </header>

      {error && <div className="error-message">{error}</div>}

      <main className="app-main">
        {view === 'patron' && <PatronDisplay patron={patron} inventory={inventory} />}
        {view === 'feed' && <FeedView patron={patron} onFeed={handleFeed} />}
        {view === 'wardrobe' && (
          <Wardrobe
            patron={patron}
            inventory={inventory}
            apiBase={API_BASE}
            onEquip={handleEquip}
            onColor={handleColor}
          />
        )}
        {view === 'shop' && (
          <ShopView patron={patron} userId={userId} onBuy={handleBuy} />
        )}
        {view === 'memories' && <MemoriesView userId={userId} apiBase={API_BASE} />}
      </main>

      <nav className="app-nav">
        {navItems.map(item => (
          <button
            key={item.id}
            className={`nav-btn ${view === item.id ? 'active' : ''}`}
            onClick={() => setView(item.id)}
            title={item.label}
          >
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

/* ---------- api helpers ---------- */

async function request(path, options = {}, timeoutMs = 8000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
      credentials: 'omit',
    });

    const body = await res.json().catch(() => ({}));

    if (!res.ok || body.error) {
      const err = new Error(body.error || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    return body;
  } finally {
    clearTimeout(timer);
  }
}

const apiGet = (path) => request(path);
const apiPost = (path, payload) =>
  request(path, { method: 'POST', body: JSON.stringify(payload) });

function describeError(err) {
  if (err.name === 'AbortError') return '⏱️ Сервер не ответил вовремя';
  if (err.message === 'not enough coins') return '💰 Не хватает монет';
  if (err.status >= 500) return '🔴 Ошибка на сервере';
  if (err.message.includes('Failed to fetch')) return '📡 Нет связи — проверь интернет';
  return `❌ ${err.message}`;
}

/* ---------- user id ---------- */

function getUserId() {
  const tg = window.Telegram?.WebApp;

  // 1. Telegram WebApp — основной путь
  const tgId = tg?.initDataUnsafe?.user?.id;
  if (tgId) {
    localStorage.setItem('user_id', String(tgId));
    return tgId;
  }

  // 2. Явный user_id в ссылке (кнопка из бота)
  const urlId = parseInt(new URLSearchParams(window.location.search).get('user_id'), 10);
  if (urlId > 0) {
    localStorage.setItem('user_id', String(urlId));
    return urlId;
  }

  // 3. Прошлая сессия
  const cached = parseInt(localStorage.getItem('user_id'), 10);
  if (cached > 0) return cached;

  return 0;
}

export default App;
